/**
 * Scroll-story stages: the single source of truth shared by the page layout
 * (Home.tsx positions copy + snap markers), the stylesheet (story height) and
 * the scene (progress detents + stage-entry triggers).
 *
 * Each stage owns one scroll slot of STAGE_SLOT_SVH; the story pins the canvas
 * for the whole run, so the total height is (N - 1) slots plus one viewport.
 * `p` is the animation progress the scene holds while the viewport rests on
 * that stage's stop.
 */
export type StageId =
  | "hero"
  | "stack"
  | "builds"
  | "emulate"
  | "test"
  | "deploy"
  | "ignition";

export type Stage = { id: StageId; p: number };

/**
 * Where the scroll story runs: wide screens (copy left, cockpit right) and
 * portrait screens, which use the compact layout (cockpit top, copy bottom).
 * Landscape phones match neither and get the static hero. cockpit.module.css
 * repeats COMPACT_QUERY as a media query; keep the two in sync.
 */
export const STORY_QUERY = "(min-width: 64em), (max-aspect-ratio: 1/1)";
export const COMPACT_QUERY = "(max-aspect-ratio: 1/1)";

export const STAGES: readonly Stage[] = [
  { id: "hero", p: 0 },
  { id: "stack", p: 0.335 },
  { id: "builds", p: 0.54 },
  { id: "emulate", p: 0.65 },
  { id: "test", p: 0.77 },
  { id: "deploy", p: 0.86 },
  { id: "ignition", p: 1 },
];

/** Scroll distance between consecutive stage stops, in small-viewport heights. */
export const STAGE_SLOT_SVH = 100;

/** Total pinned story height (svh): one slot per transition plus the viewport. */
export const STORY_HEIGHT_SVH = (STAGES.length - 1) * STAGE_SLOT_SVH + 100;

/**
 * Fraction of a slot, on each side of a stop, where progress holds still.
 * Kept small so the spring's deceleration tail (scene.ts) stays visible as
 * the stage eases into place instead of being clipped to a plateau.
 */
export const STAGE_HOLD = 0.06;

/** Scroll offset (svh from the story top) at which stage `i` is fully in view. */
export function stageStopSvh(i: number): number {
  return i * STAGE_SLOT_SVH;
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/**
 * Map raw scroll fraction (0..1 across the pinned range) to animation
 * progress. Progress plateaus at each stage's `p` around its stop and eases
 * through the transition in the middle of the slot, so a scroll-snapped
 * viewport always rests on a clean, fully-settled stage.
 */
export function detentProgress(q: number): number {
  const n = STAGES.length - 1;
  const x = Math.min(1, Math.max(0, q)) * n;
  const i = Math.min(n - 1, Math.floor(x));
  const from = STAGES[i]!;
  const to = STAGES[i + 1]!;
  const u = smoothstep(STAGE_HOLD, 1 - STAGE_HOLD, x - i);
  return from.p + (to.p - from.p) * u;
}

/**
 * Index of the stage the viewport is settling on: a stage is "entered" once
 * the transition into it is past the halfway point of its slot. Hysteresis is
 * handled by the caller.
 */
export function stageIndexAt(q: number, enterAt = 0.5): number {
  const n = STAGES.length - 1;
  const x = Math.min(1, Math.max(0, q)) * n;
  const i = Math.floor(x);
  if (i >= n) return n;
  return x - i >= enterAt ? i + 1 : i;
}

/** Fraction of a stage slot a swipe must travel to page to the next stop. */
export const PAGE_THRESHOLD = 0.15;

/** Release speed (px/ms) that pages one stop even after a short drag. */
export const FLICK_VELOCITY = 0.35;

/**
 * Touch paging: the stop index a released swipe should settle on, or null
 * when no move is needed (already on a stop, or outside the story). `stops`
 * are ascending scroll offsets one slot apart; `origin` is the index the
 * swipe started from; `velocity` is the release speed in px/ms (positive =
 * scrolling down). Moves one stop in the swipe's direction once it traveled
 * PAGE_THRESHOLD of a slot or was released at FLICK_VELOCITY (further if it
 * already traveled past a stop), else back to origin.
 */
export function pageTarget(
  stops: readonly number[],
  y: number,
  origin: number,
  velocity = 0,
): number | null {
  const first = stops[0];
  const end = stops[stops.length - 1];
  const home = stops[origin];
  const second = stops[1];
  if (first === undefined || end === undefined) return null;
  if (home === undefined || second === undefined) return null;
  if (y < first - 1 || y > end + 1) return null;
  const onHome = Math.abs(y - home) < 2;
  const f = (y - first) / (second - first);
  let next = onHome
    ? origin
    : y > home
      ? Math.ceil(f - PAGE_THRESHOLD)
      : Math.floor(f + PAGE_THRESHOLD);
  if (next === origin && Math.abs(velocity) >= FLICK_VELOCITY) {
    next = origin + Math.sign(velocity);
  }
  next = Math.min(stops.length - 1, Math.max(0, next));
  return onHome && next === origin ? null : next;
}
